/**
 * Wrapper for fetch which includes access token refreshes.
 */

import stableStringify from 'json-stable-stringify';
import {apiHost, updateStandaloneAccessToken} from '../report/util';
import {objToQueryString} from '.';
import NamedError from '../report/queries/utils/named-error';
import {fromFetchCache, updateFetchCache} from '../report/queries/utils/fetch-cache';

const DEFAULT_MAX_AUTH_RETRIES = 5;

export interface FetchOptions {
  /**
   * request's method
   */
  method?: `GET` | `POST` | `PUT` | `PATCH` | `DELETE`;
  /**
   * request's headers as a property bag
   */
  headers?: {[headerName: string]: string};
  /**
   * request's body. NOTE: this is only valid for POST/PUT/PATCH requests
   */
  body?: string | FormData;
  /**
   * extra params to send as part of underlying fetch(url, params) call
   */
  params?: Partial<RequestInit>;
  /**
   * throw ResponseError if status > 400.
   * NOTE: using .get/.post/.put/.patch/.delete will always throw for invalid responses
   * only pass throwOnInvalidStatus if calling .fetch directly
   */
  throwOnInvalidStatus?: boolean;
  /**
   * abort signal from an AbortController.
   * calling .abort() of controller will abort the request with AbortError
   */
  abortSignal?: AbortSignal;
  /**
   * query from fetch-cache, don't use network.
   * Note: will return Promise<null> if match fails.
   */
  fromFetchCache?: boolean;
  /**
   * if fetch-cache response is older than ttl seconds, Promise<null> will be returned.
   * used in conjuction when fromFetchCache:true is set or calling .hydratedGet helper.
   */
  fromFetchCacheTtl?: number;
  /**
   * when the network response comes back, update fetch-cache so it can be queried next time with fromFetchCache.
   */
  updateFetchCache?: boolean;
}

export type SuccessResponse<ResultsType> = {
  status: `ok`;
  results: ResultsType;
};

export type ErrorResponse = {
  status: `error`;
  error: string;
  type?: string;
};

export type ApiResponse<ResultsType> = ErrorResponse | SuccessResponse<ResultsType>;

class ApiResponseError extends NamedError {
  response: ApiResponse<any>;
  message: string;

  constructor(response: ApiResponse<any>, message: string) {
    super(message);
    this.response = response;
  }
}

export class ResponseError extends NamedError {
  response: Response;
  message: string;

  constructor(response: Response, message: string = null) {
    super(message);
    this.response = response;
    this.message = message || `${response.status} - ${this.getMessage(response)} (${response.url})`;
  }

  /**
   * Use response status to return a slightly more user-friendly error message.
   */
  private getMessage(response: Response) {
    switch (response.status) {
      case 400:
        return `Bad request`;
      case 401:
        return `Unauthenticated`;
      case 403:
        return `Permission denied or report contains sensitive data`;
      case 404:
        return `Resource not found`;
      case 405:
        return `Request method not allowed`;
      case 429:
        return `Too many requests - try again later`;
      case 431:
        return `Request header fields too large - try clearing your cookies and retry the request again`;
      case 500:
        return `Internal server error`;
      case 501:
        return `Not implemented`;
      case 502:
        return `Bad gateway`;
      case 503:
        return `Service unavailable`;
      case 504:
        return `Gateway timeout`;
      default:
        return `Request failed`;
    }
  }
}

function stringifyIfObj(data: string | Record<string, any>): string {
  if (data instanceof Object) {
    return JSON.stringify(data);
  }
  return data;
}

async function resultsFromResponse<ResultsType>(response: Response): Promise<ResultsType> {
  // When our app api endpoints return a response, we always return them as json.
  // However, we need to handle cases where the app api endpoint is not controlling the response
  // such as a 404, 500 or other unforseen errors.
  let responseJson: ApiResponse<ResultsType>;
  try {
    responseJson = await response.json();
  } catch (err) {
    throw new ResponseError(response);
  }

  // Check the response status. If there was an error, throw it as an Error
  switch (responseJson.status) {
    case `ok`: {
      // a valid ok response has exactly two keys in response, status and results
      // be strict and throw if the response shape doesn't match SuccessResponse
      const responseJsonKeys = Object.keys(responseJson);
      const validOkResponse = responseJsonKeys.length === 2 && responseJsonKeys.includes(`results`);
      if (!validOkResponse) {
        console.error(`response`, responseJson);
        throw new ApiResponseError(responseJson, `Invalid api response. status: 'ok' should have 'results' key`);
      }
      return responseJson.results;
    }
    case `error`: {
      // we expect `error` to always be a string but we have more work to do to enforce it programatically.
      // Some existing APIs might send back a json object instead of a string in which case
      // we'll JSON.stringify it.
      const message = stringifyIfObj(responseJson.error);
      throw new ApiResponseError(responseJson, message);
    }
    default:
      console.error(`response`, responseJson);
      throw new ApiResponseError(
        responseJson,
        // @ts-ignore Property 'status' does not exist on type 'never'
        `Invalid api response. status should be either 'ok' or 'error', got '${responseJson.status}'`,
      );
  }
}

class ApiClient {
  private refreshTokenUrl: string;
  private authRetries: number;
  private maxAuthRetries: number;
  private accessToken: string;
  private pendingResponseCache: Map<string, Promise<Response>>;

  constructor({maxAuthRetries} = {maxAuthRetries: DEFAULT_MAX_AUTH_RETRIES}) {
    this.refreshTokenUrl = `/oauth/access_token/`;
    this.authRetries = 0;
    this.maxAuthRetries = maxAuthRetries;
    this.pendingResponseCache = new Map();
  }

  get token() {
    return this.accessToken;
  }

  set token(newValue: string) {
    this.accessToken = newValue;
    updateStandaloneAccessToken(this.accessToken);
  }

  public async get<ResultsType>(
    url: string,
    query: {[key: string]: any} = null,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const apiUrl = query ? `${url}?${objToQueryString(query)}` : url;
    const response = await this.fetch(apiUrl, {method: `GET`, ...options});
    return await resultsFromResponse(response);
  }

  public async post<ResultsType>(
    url: string,
    data: string | Record<string, any>,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {
      method: `POST`,
      body: data instanceof FormData ? data : stringifyIfObj(data),
      ...options,
    });
    return await resultsFromResponse(response);
  }

  public async put<ResultsType>(
    url: string,
    data: string | Record<string, any>,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `PUT`, body: stringifyIfObj(data), ...options});
    return await resultsFromResponse(response);
  }

  public async patch<ResultsType>(
    url: string,
    data: string | Record<string, any>,
    options: FetchOptions = {},
  ): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `PATCH`, body: stringifyIfObj(data), ...options});
    return await resultsFromResponse(response);
  }

  public async delete<ResultsType>(url: string, options: FetchOptions = {}): Promise<ResultsType> {
    const response = await this.fetch(url, {method: `DELETE`, ...options});
    return await resultsFromResponse(response);
  }

  /**
   * queries fetch-cache if a cached response is available.
   * in parallel, also queries the network and updates (hydrates) the cache when network responds.
   * @example
   *  const {cache, network} = apiClient.hydratedGet(url, options);
   *  this.update({loading: true});
   *  const cacheResult = await cache;
   *  if (cacheResult !== null) {
   *    this.update({result: cachedResult, loading: false, fromCache: true});
   *  }
   *  const networkResult = await network;
   *  this.update({result: networkResult, fromCache: false});
   */
  public hydratedGet<ResultsType>(
    url: string,
    query: {[key: string]: any} = null,
    options: FetchOptions = {},
  ): {cache: Promise<ResultsType>; network: Promise<ResultsType>} {
    // according to rest principles, GET should not modify data on server, it's a pure call
    // query-api passes POST for GET like calls e.g for insights api because it expects params as a json tree
    // we make an exception and allow hydratedGet to be allowed to be called with POST for query-api.
    return options.method === `POST` && /\/api\/2.0\//.test(url)
      ? {
          cache: this.post<ResultsType>(url, query, {...options, fromFetchCache: true}),
          network: this.post<ResultsType>(url, query, {...options, updateFetchCache: true}),
        }
      : {
          cache: this.get<ResultsType>(url, query, {...options, fromFetchCache: true}),
          network: this.get<ResultsType>(url, query, {...options, updateFetchCache: true}),
        };
  }

  public async fetch(url: string, options: FetchOptions = {}): Promise<Response> {
    if (!this.token && !options.fromFetchCache) {
      await this._getNewAccessToken();
    }
    return this._fetch(url, options);
  }

  public async getAccessToken(): Promise<string> {
    if (!this.token) {
      await this._getNewAccessToken();
    }
    return this.token;
  }

  private _getNewAccessToken() {
    const url = `${apiHost()}${this.refreshTokenUrl}`;
    const fetchInit: RequestInit = {credentials: `include`};
    const pendingResponseCacheKey = stableStringify({url, fetchInit});

    if (this.pendingResponseCache.has(pendingResponseCacheKey)) {
      return this.pendingResponseCache.get(pendingResponseCacheKey);
    }

    const pendingRefresh = fetch(url, fetchInit)
      .then((response) => {
        // actually follow redirects to login page, but change 'next' to current page
        if (response.redirected) {
          const loginMatch = /.+\/login\/\?next=/.exec(response.url);
          if (loginMatch) {
            window.location.href = `${loginMatch[0]}${window.location.href}`;
          }
        }

        if (!response.ok) {
          return Promise.reject(response.statusText);
        } else {
          return response.json();
        }
      })
      .then((response) => {
        // In the case that the user is legitimately logged out, the oauth token
        // request 302s to the normal html login page, which 200s, and then
        // response.json() returns true. I wish it would fail instead, but for
        // now there's this check.
        if (!response.token) {
          // clear cached token in standalone mode so next browser refresh fetches a new one
          updateStandaloneAccessToken(null);
          return Promise.reject(`No token in response`);
        }

        this.token = response.token;
        return response.token;
      });

    this.pendingResponseCache.set(pendingResponseCacheKey, pendingRefresh);
    pendingRefresh.finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
    return pendingRefresh;
  }

  private _fetch(url: string, fetchOptions: FetchOptions): Promise<Response> {
    const {
      headers = {},
      method = `GET`,
      body = null,
      params = {},
      throwOnInvalidStatus = false,
      abortSignal: signal = null,
    } = fetchOptions || {};

    const fetchInit: RequestInit = {
      headers: {
        Authorization: `Bearer ${this.token}`,
        ...headers,
      },
      method,
      body,
      signal,
      ...params,
    };

    if (fetchOptions.fromFetchCache) {
      return fromFetchCache(url, fetchInit, fetchOptions.fromFetchCacheTtl);
    }

    const pendingResponseCacheKey = stableStringify({url, fetchInit});
    if (this.pendingResponseCache.has(pendingResponseCacheKey)) {
      // we clone the response because body can only be read once.
      // multiple callers calling response.json() will fail with 'body stream is locked' error
      return this.pendingResponseCache.get(pendingResponseCacheKey).then((response) => response.clone());
    }

    const responsePromise = fetch(url, fetchInit).then((response) => {
      if (throwOnInvalidStatus && response.status >= 400) {
        throw new ResponseError(response);
      }

      if (response.status >= 500) {
        // we always throw for 500-ish error
        this.authRetries = 0;
        throw new ResponseError(response);
      } else if (response.status === 401) {
        this.token = null;
        if (this.authRetries++ < this.maxAuthRetries) {
          return this.fetch(url, fetchOptions);
        } else {
          throw new Error(`Authorization failed, ${this.authRetries} retries`);
        }
      } else {
        // underlying updateFetchCache will only update if response.ok and response.status === 200
        if (fetchOptions.updateFetchCache) {
          updateFetchCache(url, fetchInit, response);
        }
        this.authRetries = 0;
        return response;
      }
    });

    this.pendingResponseCache.set(pendingResponseCacheKey, responsePromise);
    responsePromise.finally(() => this.pendingResponseCache.delete(pendingResponseCacheKey));
    return responsePromise;
  }
}

let apiClient: ApiClient;
if (typeof window !== `undefined`) {
  // initialize as window singleton that is shared between bundles
  apiClient = window[`__apiClient`] = window[`__apiClient`] || new ApiClient();
} else {
  // probably in unit test env, module level singleton
  apiClient = new ApiClient();
}

export {apiClient, ApiClient};
